import { Category } from '@modules/husleie-framework/src/enums/Category.enum';
import { UserAppellationKey } from '@modules/husleie-framework/src/enums/UserAppellation.enum';
import { UserTitleKey } from '@modules/husleie-framework/src/enums/UserTitle.enum';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import { Contract } from '@modules/husleie-framework/src/types/Contract.entity';
import { RentalAdjustmentTypeEnum } from '@modules/husleie-framework/src/types/ContractRentalAdjustment.entity';
import { Landlord, LandlordTypeEnum } from '@modules/husleie-framework/src/types/Landlord.entity';
import type { PrivateLandlord } from '@modules/husleie-framework/src/types/PrivateLandlord.entity';
import type { ProfessionalLandlord } from '@modules/husleie-framework/src/types/ProfessionalLandlord.entity';
import { RentDurationEnum, LimitedRentDurationEnum } from '@modules/husleie-framework/src/types/Rent.entity';
import type { RentalObject } from '@modules/husleie-framework/src/types/RentalObject.entity';
import { RentalObjectFurnishingStandardEnum } from '@modules/husleie-framework/src/types/RentalObject.entity';
import { Session, SessionTypeEnum } from '@modules/husleie-framework/src/types/Session.entity';
import { SigningStatusEnum, SignMethodEnum } from '@modules/husleie-framework/src/types/Signing.entity';
import { UserRoleEnum } from '@modules/husleie-framework/src/types/User.entity';
import { UtilityTypeEnum, UtilityStatusEnum } from '@modules/husleie-framework/src/types/Utility';
import type {
  LandlordAPIInterface,
  LandlordCreate,
  LandlordCreateResponse,
  LandlordGet,
  LandlordGetResponse,
  LandlordProfileAddressGet,
  LandlordProfileAddressGetResponse,
  LandlordProfileUpdate,
  LandlordRentalObjectsGet,
  LandlordRentalObjectsGetResponse,
  LandlordContractsGet,
  LandlordContractsGetResponse,
  LandlordContractDelete,
  LandlordContractDeleteResponse,
  LandlordInspectionReportsGet,
  LandlordInspectionReportsGetResponse,
  LandlordDownloadTokenGet,
  LandlordDownloadTokenGetResponse,
  LandlordTenancyGet,
  LandlordTenancyGetResponse,
  LandlordInspectionReportDelete,
  LandlordInspectionReportDeleteResponse,
  LandlordDownloadSessionGet,
  LandlordDownloadSessionGetResponse,
  LandlordTenanciesGet,
  LandlordTenanciesGetResponse,
  LandlordTenancySubscriptionsGet,
  LandlordTenancySubscriptionsGetResponse,
  TenancyDocument,
} from '@modules/store/landlord/types/LandlordAPI.interface';
import {
  LandlordTenancyDocumentsGet,
  LandlordTenancyDocumentsGetResponse,
  LandlordTenancySubscriptionsUpdate,
} from '@modules/store/landlord/types/LandlordAPI.interface';
import { Tenancy, TenancyStatus } from '@modules/husleie-framework/src/types/Tenancy.entity';
import {
  SubscriptionPaymentInterval,
  SubscriptionCurrency,
  Subscription,
} from '@modules/design-system/components/Subscription/Subscription.entity';
import { TenantType } from '@modules/husleie-framework/src/types/Tenant.entity';
import { PaymentMethodEnum, Payment } from '@modules/husleie-framework/src/types/Payment.entity';

export default class API implements LandlordAPIInterface {
  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  createLandlord(create: LandlordCreate): Promise<LandlordCreateResponse> {
    const data: PrivateLandlord | ProfessionalLandlord = {
      id: 10,
      type: LandlordTypeEnum.PRIVATE,
    };

    LogOperations.debug('MOCK signup::createLandlord', JSON.stringify(data));

    return new Promise(resolve => resolve({ landlord: data }));
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  getLandlord(get: LandlordGet): Promise<LandlordGetResponse> {
    const data: Landlord = {
      type: LandlordTypeEnum.PRIVATE,
      id: 10,
      user: {
        firstName: 'Example',
        lastName: 'Landlord',
        fullName: 'Example Landlord',
        email: 'example@landlord.com',
        role: UserRoleEnum.LANDLORD,
        appellation: UserAppellationKey.MSS,
      },
    };

    LogOperations.debug('MOCK landlord::getLandlord', JSON.stringify(data));

    return new Promise(resolve => resolve({ landlord: data, isSudo: false }));
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  getLandlordRentalObjects(get: LandlordRentalObjectsGet): Promise<LandlordRentalObjectsGetResponse> {
    const data: RentalObject[] = [
      {
        id: 10,
        category: Category.APARTMENT,
        address: {
          street: 'Tängvägen',
          apartmentLocation:
            'Kitty scratches couch bad kitty hopped up on catnip destroy couch, or break lamps and curl up into a ball. ',
          roomNumber: '12',
          floor: '4',
          postalCode: '',
          postalArea: '',
          country: {
            code: 'de',
          },
          fullAddress: 'M Street, 11111, M City',
        },
      },
      {
        id: 11,
        category: Category.APARTMENT,
        address: {
          apartmentLocation:
            "Sniff other cat's butt and hang jaw half open thereafter. Loves cheeseburgers get scared by doggo also cucumerro adventure always.",
          roomNumber: '12',
          floor: '4',
          street: 'Edina Szilvia Papp gate',
          postalCode: '',
          postalArea: '',
          country: {
            code: 'de',
          },
          fullAddress: 'M Street, 11111, M City',
        },
      },
      {
        id: 12,
        category: Category.DETACHED_HOUSE,
        address: {
          street: 'Zlatan Strasse',
          roomNumber: '12',
          floor: '4',
          postalCode: '11111',
          postalArea: 'Zlatan City',
          country: {
            code: 'de',
          },
          fullAddress: 'M Street, 11111, M City',
        },
      },
    ];

    LogOperations.debug('MOCK landlord::getLandlordRentalObjects', JSON.stringify(data));

    return new Promise(resolve => resolve({ rentalObjects: data }));
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  getLandlordContracts(get: LandlordContractsGet): Promise<LandlordContractsGetResponse> {
    const data: Contract[] = [
      {
        uuid: '3fdf4ce8-f229-4fa1-817e-contract1',
        status: SigningStatusEnum.OPEN,
        landlord: {
          type: LandlordTypeEnum.PRIVATE,
          id: 37,
          user: {
            firstName: 'Example',
            lastName: 'Landlord',
            fullName: 'Example Landlord',
            email: 'example@landlord.com',
            role: UserRoleEnum.LANDLORD,
            appellation: UserAppellationKey.MSS,
          },
        },
        tenant: {
          user: {
            id: 10,
            firstName: 'Zlatan',
            lastName: 'Ibrahimović',
            fullName: 'Zlatan Ibrahimović',
            email: 'Zlatan.Ibrahimović@hotshots.com',
            dateOfBirth: '01.01.2000',
            appellation: UserAppellationKey.MR,
            role: UserRoleEnum.TENANT,
          },
          address: {
            street: 'M Street',
            postalCode: '11111',
            postalArea: 'M City',
            country: {
              code: 'XX',
            },
            fullAddress: 'M Street, 11111, M City',
          },
        },
        tenantFeedback: '',
        coTenants: [],
        amountOfResidents: 1,
        rentalObject: {
          id: 10,
          category: Category.APARTMENT,
          address: {
            street: 'Zlatan Strasse 1',
            additionalAddress: '',
            apartmentLocation: 'second floor',
            postalCode: '11111',
            postalArea: 'Zlatan City',
            country: {
              code: 'DE',
            },
            fullAddress: 'M Street, 11111, M City',
          },
          furnishingStandard: RentalObjectFurnishingStandardEnum.FURNISHED,
          rooms: [],
          furniture: [],
          storageDetail: [],
          keychain: {
            keys: [],
          },
          houseRulesUploads: [],
          additionalDocuments: [],
          useDefaultHouseRules: false,
          parkingFacilities: false,
        },
        rentalAdjustment: {
          rentalAdjustment: RentalAdjustmentTypeEnum.COMPARABLE_RENT,
          rentRegulation: false,
          tiers: [],
          firstUsedAndRented: true,
          renovationsLastThreeYears: true,
          occupancyAfterRenovations: true,
        },
        deposit: {
          ownDefinedAmount: 1000,
          ownDefined: false,
          months: 3,
          depositSolutions: [],
        },
        rentalCalculation: {
          additionalCost: 'combined',
          additionalCostTypes: false,
          utilityCostBilling: 'calendarYear',
          utilityItems: [],
          heatingCostBilling: 'calendarYear',
        },
        sign: SignMethodEnum.DIGITAL,
        rent: {
          durationType: RentDurationEnum.UNLIMITED,
          limitedRentDurationType: LimitedRentDurationEnum.OWN_USE,
        },
        mode: 'created',
        modified: '01.09.2021',
        pdfLink: 'https://smartmiete.de/docs/hausordnung.pdf',
      },
      {
        uuid: '3fdf4ce8-f229-4fa1-817e-contract2',
        status: SigningStatusEnum.SIGNED,
        landlord: {
          type: LandlordTypeEnum.PRIVATE,
          id: 37,
          user: {
            firstName: 'Example',
            lastName: 'Landlord',
            fullName: 'Example Landlord',
            email: 'example@landlord.com',
            role: UserRoleEnum.LANDLORD,
            appellation: UserAppellationKey.MSS,
          },
        },
        tenant: {
          user: {
            id: 10,
            firstName: 'Zlatan',
            lastName: 'Ibrahimović',
            fullName: 'Zlatan Ibrahimović as Contract',
            email: 'Zlatan.Ibrahimović@hotshots.com',
            dateOfBirth: '01.01.2000',
            appellation: UserAppellationKey.MR,
            role: UserRoleEnum.TENANT,
          },
          address: {
            street: 'M Street',
            postalCode: '11111',
            postalArea: 'M City',
            country: {
              code: 'XX',
            },
            fullAddress: 'M Street, 11111 M City',
          },
        },
        tenantFeedback: '',
        coTenants: [],
        amountOfResidents: 1,
        rentalObject: {
          id: 11,
          category: Category.APARTMENT,
          address: {
            street: 'Zlatan Strasse 1',
            additionalAddress: '',
            apartmentLocation: 'second floor',
            postalCode: '11111',
            postalArea: 'Zlatan City',
            country: {
              code: 'DE',
            },
            fullAddress: 'M Street, 11111, M City',
          },
          furnishingStandard: RentalObjectFurnishingStandardEnum.FURNISHED,
          rooms: [],
          furniture: [],
          storageDetail: [],
          keychain: {
            keys: [],
          },
          houseRulesUploads: [],
          additionalDocuments: [],
          useDefaultHouseRules: false,
          parkingFacilities: false,
        },
        rentalAdjustment: {
          rentalAdjustment: RentalAdjustmentTypeEnum.COMPARABLE_RENT,
          rentRegulation: false,
          tiers: [],
          firstUsedAndRented: true,
          renovationsLastThreeYears: true,
          occupancyAfterRenovations: true,
        },
        deposit: {
          ownDefinedAmount: 1000,
          ownDefined: false,
          months: 3,
          depositSolutions: [],
        },
        rentalCalculation: {
          additionalCost: 'combined',
          additionalCostTypes: false,
          utilityCostBilling: 'calendarYear',
          utilityItems: [],
          heatingCostBilling: 'calendarYear',
        },
        sign: SignMethodEnum.DIGITAL,
        rent: {
          startDate: '01.01.2022',
          endDate: '31.12.2022',
          durationType: RentDurationEnum.UNLIMITED,
          limitedRentDurationType: LimitedRentDurationEnum.OWN_USE,
        },
        mode: 'created',
        modified: '01.10.2021',
        pdfLink: 'https://smartmiete.de/docs/hausordnung.pdf',
      },
      {
        uuid: '3fdf4ce8-f229-4fa1-817e-contract3',
        status: SigningStatusEnum.SIGNING_LANDLORD,
        landlord: {
          type: LandlordTypeEnum.PRIVATE,
          id: 37,
          user: {
            firstName: 'Example',
            lastName: 'Landlord',
            fullName: 'Example Landlord',
            email: 'example@landlord.com',
            role: UserRoleEnum.LANDLORD,
            appellation: UserAppellationKey.MSS,
          },
        },
        tenant: {
          user: {
            id: 10,
            firstName: 'Zlatan',
            lastName: 'Ibrahimović',
            fullName: 'Zlatan Ibrahimović',
            email: 'Zlatan.Ibrahimović@hotshots.com',
            dateOfBirth: '01.01.2000',
            appellation: UserAppellationKey.MR,
            role: UserRoleEnum.TENANT,
          },
          address: {
            street: 'M Street',
            postalCode: '11111',
            postalArea: 'M City',
            country: {
              code: 'XX',
            },
            fullAddress: 'M Street, 11111, M City',
          },
        },
        tenantFeedback: '',
        coTenants: [],
        amountOfResidents: 1,
        rentalObject: {
          id: 12,
          category: Category.APARTMENT,
          furnishingStandard: RentalObjectFurnishingStandardEnum.FURNISHED,
          rooms: [],
          furniture: [],
          storageDetail: [],
          keychain: {
            keys: [],
          },
          houseRulesUploads: [],
          additionalDocuments: [],
          useDefaultHouseRules: false,
          parkingFacilities: false,
        },
        rentalAdjustment: {
          rentalAdjustment: RentalAdjustmentTypeEnum.COMPARABLE_RENT,
          rentRegulation: false,
          tiers: [],
          firstUsedAndRented: true,
          renovationsLastThreeYears: true,
          occupancyAfterRenovations: true,
        },
        deposit: {
          ownDefinedAmount: 1000,
          ownDefined: false,
          months: 3,
          depositSolutions: [],
        },
        rentalCalculation: {
          additionalCost: 'combined',
          additionalCostTypes: false,
          utilityCostBilling: 'calendarYear',
          utilityItems: [],
          heatingCostBilling: 'calendarYear',
        },
        sign: SignMethodEnum.DIGITAL,
        rent: {
          durationType: RentDurationEnum.UNLIMITED,
          limitedRentDurationType: LimitedRentDurationEnum.OWN_USE,
        },
        mode: 'created',
        modified: '01.01.2022',
        pdfLink: 'https://smartmiete.de/docs/hausordnung.pdf',
      },
    ];

    LogOperations.debug('API|MOCK landlord contracts::GET response', data);

    return new Promise(resolve => resolve({ contracts: data }));
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  getLandlordInspectionReports(get: LandlordInspectionReportsGet): Promise<LandlordInspectionReportsGetResponse> {
    const data: Session[] = [
      {
        uuid: '3fdf4ce8-f229-4fa1-817e-ir1',
        type: SessionTypeEnum.MOVE_IN,
        status: SigningStatusEnum.OPEN,
        landlord: {
          type: LandlordTypeEnum.PRIVATE,
          id: 37,
          user: {
            firstName: 'Example',
            lastName: 'Landlord',
            fullName: 'Example Landlord',
            email: 'example@landlord.com',
            role: UserRoleEnum.LANDLORD,
            appellation: UserAppellationKey.MSS,
          },
        },
        tenant: {
          user: {
            id: 10,
            firstName: 'Zlatan',
            lastName: 'Ibrahimović',
            fullName: 'Zlatan Ibrahimović',
            email: 'Zlatan.Ibrahimović@hotshots.com',
            appellation: UserAppellationKey.MR, // 'Mister'
            title: UserTitleKey.PROF_DR, // 'Prof. Dr.'
            role: UserRoleEnum.TENANT, // 'TENANT'
          },
          address: {
            street: 'Street 1',
            additionalAddress: '',
            postalCode: '11111',
            postalArea: 'M City',
            country: {
              code: 'DE',
            },
            fullAddress: 'M Street, 11111, M City',
          },
        },
        coTenants: [],
        rentalObject: {
          category: Category.APARTMENT, // 'APARTMENT'
          address: {
            street: 'Zlatan Strasse 1',
            additionalAddress: '',
            apartmentLocation: 'second floor',
            postalCode: '11111',
            postalArea: 'Zlatan City',
            country: {
              code: 'DE',
            },
            fullAddress: 'M Street, 11111, M City',
          },
          furnishingStandard: RentalObjectFurnishingStandardEnum.FURNISHED,
          rooms: [
            {
              name: 'Kitchen',
              images: [
                {
                  token: 'token1',
                  imageSrc: 'http://placekitten.com/201/100',
                },
                {
                  token: 'token2',
                  imageSrc: 'http://placekitten.com/200/201',
                },
                {
                  token: 'token3',
                  imageSrc: 'http://placekitten.com/200/202',
                },
                {
                  token: 'token4',
                  imageSrc: 'http://placekitten.com/200/203',
                },
                {
                  token: 'token5',
                  imageSrc: 'http://placekitten.com/200/204',
                },
              ],
            },
            {
              name: 'Living Room',
              images: [
                {
                  token: 'token6',
                  imageSrc: 'http://placekitten.com/201/100',
                },
                {
                  token: 'token7',
                  imageSrc: 'http://placekitten.com/200/201',
                },
                {
                  token: 'token8',
                  imageSrc: 'http://placekitten.com/200/202',
                },
                {
                  token: 'token9',
                  imageSrc: 'http://placekitten.com/200/203',
                },
                {
                  token: 'token10',
                  imageSrc: 'http://placekitten.com/200/204',
                },
              ],
            },
          ],
          keychain: {
            keys: [
              {
                name: 'Front door',
                quantity: 2,
              },
              {
                name: 'Back door',
                quantity: 2,
              },
            ],
          },
          utilities: [
            {
              type: UtilityTypeEnum.ELECTRICITY,
              state: UtilityStatusEnum.RELEVANT,
              meter: {
                number: 'si39wjsjh3783',
                reading: {
                  date: '2021-12-10',
                  value: 8833,
                  images: [
                    {
                      token: 'token11',
                      imageSrc: 'http://placekitten.com/201/100',
                    },
                  ],
                  comment: '',
                },
              },
            },
            {
              type: UtilityTypeEnum.COLD_WATER,
              state: UtilityStatusEnum.RELEVANT,
              meter: {
                reading: {
                  date: '2021-12-10',
                  images: [
                    {
                      token: 'token12',
                      imageSrc: 'http://placekitten.com/201/100',
                    },
                  ],
                  comment: '',
                },
              },
            },
            {
              type: UtilityTypeEnum.HOT_WATER,
              state: UtilityStatusEnum.NOT_RELEVANT,
              meter: {
                reading: {
                  images: [],
                },
              },
            },
          ],
          details: {
            amountOfResidents: 1,
            furnishingStandard: RentalObjectFurnishingStandardEnum.UNFURNISHED,
            moveInDate: '07.12.2021',
          },
        },
        comment: '',
        images: [],
        modified: '01.12.2021',
        pdfLink: 'https://smartmiete.de/docs/hausordnung.pdf',
      },
      {
        uuid: '3fdf4ce8-f229-4fa1-817e-ir2',
        type: SessionTypeEnum.MOVE_IN,
        status: SigningStatusEnum.SIGNING,
        landlord: {
          type: LandlordTypeEnum.PRIVATE,
          id: 37,
          user: {
            firstName: 'Example',
            lastName: 'Landlord',
            fullName: 'Example Landlord',
            email: 'example@landlord.com',
            role: UserRoleEnum.LANDLORD,
            appellation: UserAppellationKey.MSS,
          },
        },
        tenant: {
          user: {
            id: 10,
            firstName: 'Zlatan',
            lastName: 'Ibrahimović',
            fullName: 'Zlatan Ibrahimović',
            email: 'Zlatan.Ibrahimović@hotshots.com',
            appellation: UserAppellationKey.MR, // 'Mister'
            title: UserTitleKey.PROF_DR, // 'Prof. Dr.'
            role: UserRoleEnum.TENANT, // 'TENANT'
          },
          address: {
            street: 'Street 1',
            additionalAddress: '',
            postalCode: '11111',
            postalArea: 'M City',
            country: {
              code: 'DE',
            },
          },
        },
        coTenants: [],
        rentalObject: {
          category: Category.APARTMENT, // 'APARTMENT'
          address: {
            street: 'Zlatan Strasse 2',
            additionalAddress: '',
            apartmentLocation: 'second floor',
            postalCode: '11111',
            postalArea: 'Zlatan City',
            country: {
              code: 'DE',
            },
          },
          furnishingStandard: RentalObjectFurnishingStandardEnum.FURNISHED,
          rooms: [
            {
              name: 'Kitchen',
              images: [
                {
                  token: 'token1',
                  imageSrc: 'http://placekitten.com/201/100',
                },
                {
                  token: 'token2',
                  imageSrc: 'http://placekitten.com/200/201',
                },
                {
                  token: 'token3',
                  imageSrc: 'http://placekitten.com/200/202',
                },
                {
                  token: 'token4',
                  imageSrc: 'http://placekitten.com/200/203',
                },
                {
                  token: 'token5',
                  imageSrc: 'http://placekitten.com/200/204',
                },
              ],
            },
            {
              name: 'Living Room',
              images: [
                {
                  token: 'token6',
                  imageSrc: 'http://placekitten.com/201/100',
                },
                {
                  token: 'token7',
                  imageSrc: 'http://placekitten.com/200/201',
                },
                {
                  token: 'token8',
                  imageSrc: 'http://placekitten.com/200/202',
                },
                {
                  token: 'token9',
                  imageSrc: 'http://placekitten.com/200/203',
                },
                {
                  token: 'token10',
                  imageSrc: 'http://placekitten.com/200/204',
                },
              ],
            },
          ],
          keychain: {
            keys: [
              {
                name: 'Front door',
                quantity: 2,
              },
              {
                name: 'Back door',
                quantity: 2,
              },
            ],
          },
          utilities: [
            {
              type: UtilityTypeEnum.ELECTRICITY,
              state: UtilityStatusEnum.RELEVANT,
              meter: {
                number: 'si39wjsjh3783',
                reading: {
                  date: '10.12.2021',
                  value: 8833,
                  images: [
                    {
                      token: 'token11',
                      imageSrc: 'http://placekitten.com/201/100',
                    },
                  ],
                  comment: '',
                },
              },
            },
            {
              type: UtilityTypeEnum.COLD_WATER,
              state: UtilityStatusEnum.RELEVANT,
              meter: {
                reading: {
                  date: '10.12.2021',
                  images: [
                    {
                      token: 'token12',
                      imageSrc: 'http://placekitten.com/201/100',
                    },
                  ],
                  comment: '',
                },
              },
            },
            {
              type: UtilityTypeEnum.HOT_WATER,
              state: UtilityStatusEnum.NOT_RELEVANT,
              meter: {
                reading: {
                  images: [],
                },
              },
            },
          ],
          details: {
            amountOfResidents: 1,
            furnishingStandard: RentalObjectFurnishingStandardEnum.UNFURNISHED,
            moveInDate: '07.12.2021',
          },
        },
        comment: '',
        images: [],
        modified: '10.01.2022',
        pdfLink: 'https://smartmiete.de/docs/hausordnung.pdf',
      },
    ];

    LogOperations.debug('API|MOCK landlord inspectionReports::GET InspectionReports response', data);

    return new Promise(resolve => resolve({ inspectionReports: data }));
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  getLandlordTenancy(get: LandlordTenancyGet): Promise<LandlordTenancyGetResponse> {
    const data: Tenancy = {
      id: 1,
      status: TenancyStatus.ACTIVE,
      subscription: {
        subscriptionPlanId: 2,
        subscriptionPlanName: 'basic',
        price: 5,
        currency: SubscriptionCurrency.EUR,
        paymentInterval: SubscriptionPaymentInterval.MONTH,
      },
      startDate: '01.01.2022',
      endDate: '31.12.2022',
      tenants: [
        {
          type: TenantType.TENANT,
          user: {
            id: 10,
            firstName: 'Zlatan',
            lastName: 'Ibrahimović',
            fullName: 'Zlatan Ibrahimović',
            email: 'Zlatan.Ibrahimović@hotshots.com',
            dateOfBirth: '01.01.2000',
            appellation: UserAppellationKey.MR,
            role: UserRoleEnum.TENANT,
          },
        },
      ],
      rentalObjectId: 10,
      rentalObject: {
        id: 10,
        category: Category.APARTMENT,
        address: {
          street: 'Zlatan Strasse 1',
          additionalAddress: '',
          apartmentLocation: 'second floor',
          postalCode: '11111',
          postalArea: 'Zlatan City',
          country: {
            code: 'DE',
          },
          fullAddress: 'M Street, 11111, M City',
        },
      },
    };

    LogOperations.debug('API|MOCK landlord tenancy:GET Tenancy response', data);

    return new Promise(resolve => resolve({ tenancy: data }));
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  getLandlordTenancies(get: LandlordTenanciesGet): Promise<LandlordTenanciesGetResponse> {
    const data: Tenancy[] = [
      {
        id: 1,
        status: TenancyStatus.ACTIVE,
        subscription: {
          subscriptionPlanId: 2,
          subscriptionPlanName: 'basic',
          price: 5,
          currency: SubscriptionCurrency.EUR,
          paymentInterval: SubscriptionPaymentInterval.MONTH,
        },
        startDate: '01.01.2022',
        endDate: '31.12.2022',
        tenants: [
          {
            type: TenantType.TENANT,
            user: {
              id: 10,
              firstName: 'Zlatan',
              lastName: 'Ibrahimović',
              fullName: 'Zlatan Ibrahimović as Tenancy',
              email: 'Zlatan.Ibrahimović@hotshots.com',
              dateOfBirth: '01.01.2000',
              appellation: UserAppellationKey.MR,
              role: UserRoleEnum.TENANT,
            },
          },
        ],
        rentalObjectId: 10,
        rentalObject: {
          id: 10,
          category: Category.APARTMENT,
          address: {
            street: 'Zlatan Strasse 1',
            additionalAddress: '',
            apartmentLocation: 'second floor',
            postalCode: '11111',
            postalArea: 'Zlatan City',
            country: {
              code: 'DE',
            },
            fullAddress: 'M Street, 11111, M City',
          },
        },
      },
    ];

    LogOperations.debug('API|MOCK landlord tenancies::GET Tenancies response', data);

    return new Promise(resolve => resolve({ tenancies: data }));
  }

  getLandlordTenancySubscriptions(
    /* eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars */
    get: LandlordTenancySubscriptionsGet
  ): Promise<LandlordTenancySubscriptionsGetResponse> {
    const payment: Payment = {
      method: PaymentMethodEnum.STRIPE,
      stripe: {
        clientSecret: 'seti_1LBktkHlp2QI6XVXW3WhlsAK_secret_LtXzklB5RZlDVG68B1qDmvDpjnW2eTj',
        publicKey: 'pk_test_dNeua2sJeN2uF63agbY5mBHM',
      },
    };
    const subscriptionPlans: Subscription[] = [
      {
        id: 1,
        name: 'free',
        description: 'free plan',
        isPrimaryChoice: false,
        paymentInterval: SubscriptionPaymentInterval.MONTH,
        price: 0,
        vat: {
          vatPercentage: 19,
          vatTotal: 1.12,
        },
        currency: SubscriptionCurrency.EUR,
      },
      {
        id: 3,
        name: 'premium',
        description: 'premium plan',
        isPrimaryChoice: true,
        paymentInterval: SubscriptionPaymentInterval.MONTH,
        price: 7,
        vat: {
          vatPercentage: 19,
          vatTotal: 1.12,
        },
        currency: SubscriptionCurrency.EUR,
      },
    ];

    LogOperations.debug(
      'MOCK landlord::getLandlordTenancySubscriptions',
      JSON.stringify({ payment, subscriptionPlans })
    );

    return new Promise(resolve => resolve({ payment, subscriptionPlans }));
  }

  updateLandlordTenancySubscription(update: LandlordTenancySubscriptionsUpdate): Promise<void> {
    LogOperations.debug('MOCK landlord::updateLandlordTenancySubscription', JSON.stringify(update));

    return new Promise(resolve => resolve());
  }

  deleteLandlordContract(remove: LandlordContractDelete): Promise<LandlordContractDeleteResponse> {
    LogOperations.debug('MOCK landlord::deleteLandlordContract', remove.uuid);

    return new Promise(resolve => resolve({}));
  }

  deleteLandlordInspectionReport(
    remove: LandlordInspectionReportDelete
  ): Promise<LandlordInspectionReportDeleteResponse> {
    LogOperations.debug('MOCK landlord::deleteLandlordInspectionReport', remove.uuid);

    return new Promise(resolve => resolve({}));
  }

  /**
   * Get landlord postal address
   *
   * @param get LandlordProfileAddressGet
   * @returns
   */
  async getProfileLandlordPostalAddress(get: LandlordProfileAddressGet): Promise<LandlordProfileAddressGetResponse> {
    LogOperations.debug('MOCK landlord::getProfileLandlordPostalAddress', get.landlord.id);

    return new Promise(resolve =>
      resolve({ address: { street: 'X street', postalCode: 'xxxx', postalArea: 'XXXX', country: { code: 'XX' } } })
    );
  }

  /**
   * Update landlord postal address
   * @param update LandlordProfileUpdate
   */
  async patchProfileLandlord(update: LandlordProfileUpdate): Promise<void> {
    LogOperations.debug('MOCK landlord::patchProfileLandlord', update.landlord.id);

    return new Promise(resolve => resolve());
  }

  getLandlordDownLoadToken(get: LandlordDownloadTokenGet): Promise<LandlordDownloadTokenGetResponse> {
    const data = { token: 'token' };

    LogOperations.debug('API|MOCK landlord::GET getLandlordDownLoadToken request', get);
    LogOperations.debug('API|MOCK landlord::GET getLandlordDownLoadToken response', data);

    return Promise.resolve(data);
  }

  getLandlordDownLoadSession(get: LandlordDownloadSessionGet): Promise<LandlordDownloadSessionGetResponse> {
    const data = { pdf: new Blob(['testing'], { type: 'application/pdf' }) };

    LogOperations.debug('API|MOCK landlord::GET getLandlordDownLoadSession request', get);
    LogOperations.debug('API|MOCK landlord::GET getLandlordDownLoadSession response', data);

    return Promise.resolve(data);
  }

  getLandlordTenancyDocuments(get: LandlordTenancyDocumentsGet): Promise<LandlordTenancyDocumentsGetResponse> {
    const data = {
      documents: [] as TenancyDocument[],
    };

    if (get.landlordId === 10 && get.tenancyId === 1) {
      data.documents = [
        {
          token: 'token',
          category: 'contract',
          contentType: 'pdf',
          size: 1000,
          id: 1,
          created: '',
          fileName: 'contract',
          mediaLinks: [
            {
              width: 200,
              url: 'https://smartmiete.de/docs/hausordnung.pdf',
              type: 'original',
              contentType: 'pdf',
            },
          ],
        },
        {
          token: 'token',
          category: 'inspection_report',
          contentType: 'pdf',
          size: 1000,
          id: 1,
          created: '',
          fileName: 'contract',
          mediaLinks: [
            {
              width: 200,
              url: 'https://smartmiete.de/docs/hausordnung.pdf',
              type: 'original',
              contentType: 'pdf',
            },
          ],
        },
      ];
    }

    LogOperations.debug('API|MOCK landlord::GET getLandlordTenancyDocuments request', get);
    LogOperations.debug('API|MOCK landlord::GET getLandlordTenancyDocuments response', data);

    return Promise.resolve(data);
  }
}
